import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import api from '../config/api';

interface Watchlist {
  _id: string;
  name: string;
  description: string;
  createdBy: string;
  movies: any[];
  isActive: boolean;
}

const Watchlists: React.FC = () => {
  const [watchlists, setWatchlists] = useState<Watchlist[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [watchlistToDelete, setWatchlistToDelete] = useState<string | null>(null);
  const navigate = useNavigate();

  const fetchWatchlists = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/api/watchlists');
      setWatchlists(response.data);
    } catch (error) {
      console.error('Error fetching watchlists:', error);
      setError('Failed to fetch watchlists');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWatchlists();
  }, []);

  const handleDeleteClick = (watchlistId: string) => {
    setWatchlistToDelete(watchlistId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!watchlistToDelete) return;

    try {
      setLoading(true);
      setError('');
      await api.delete(`/api/watchlists/${watchlistToDelete}`);
      await fetchWatchlists();
      setDeleteDialogOpen(false);
      setWatchlistToDelete(null);
    } catch (error) {
      console.error('Error deleting watchlist:', error);
      setError('Failed to delete watchlist');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setWatchlistToDelete(null);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
        <Typography variant="h4" component="h1">
          Watchlists
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate('/watchlists/new')}
        >
          Create New Watchlist
        </Button>
      </Box>

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <Grid container spacing={3}>
        {watchlists.map((watchlist) => (
          <Grid item xs={12} sm={6} md={4} key={watchlist._id}>
            <Card>
              <CardContent>
                <Typography variant="h6" component="h2">
                  {watchlist.name}
                </Typography>
                <Typography color="text.secondary" gutterBottom>
                  {watchlist.description}
                </Typography>
                <Typography variant="body2">
                  {watchlist.movies.length} movies
                </Typography>
              </CardContent>
              <CardActions>
                <Button
                  size="small"
                  onClick={() => navigate(`/watchlists/${watchlist._id}`)}
                >
                  View Details
                </Button>
                {watchlist.isActive && (
                  <Button size="small" color="secondary">
                    Vote Now
                  </Button>
                )}
                <Button
                  size="small"
                  color="error"
                  onClick={() => handleDeleteClick(watchlist._id)}
                >
                  Delete
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>Delete Watchlist</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this watchlist? This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" disabled={loading}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Watchlists; 